/**
 * Calendar feature — Ducks pattern (actions, reducer, types).
 */

import { CalendarEvent } from '@/infra/types/domain';

// ─── State Shape ───────────────────────────────────────────

export type CalendarViewMode = 'day' | 'week' | 'month' | 'year';

export interface CalendarState {
  events: CalendarEvent[];
  selectedDate: string | null; // ISO date string
  viewMode: CalendarViewMode;
  loading: boolean;
  error: string | null;
  lastSyncAt: number | null;
}

export const initialState: CalendarState = {
  events: [],
  selectedDate: null,
  viewMode: 'week',
  loading: false,
  error: null,
  lastSyncAt: null,
};

// ─── Action Types ──────────────────────────────────────────

const SET_EVENTS = 'calendar/setEvents';
const ADD_EVENT = 'calendar/addEvent';
const UPDATE_EVENT = 'calendar/updateEvent';
const REPLACE_EVENT = 'calendar/replaceEvent';
const REMOVE_EVENT = 'calendar/removeEvent';
const SET_SELECTED_DATE = 'calendar/setSelectedDate';
const SET_VIEW_MODE = 'calendar/setViewMode';
const SET_LOADING = 'calendar/setCalendarLoading';
const SET_ERROR = 'calendar/setCalendarError';
// In-place projection of a subcalendar mutation onto every event with
// that subcalendar_id. Lets updateSubcalendarSaga avoid a fetchEvents()
// hammer when only color / assigned_owner_id / enabled flipped on the
// subcalendar row — per feedback_saga_full_delta the saga should
// dispatch the post-write delta, not refetch.
export const EVENTS_PATCHED_BY_SUBCAL = 'calendar/eventsPatchedBySubcalendar';
// Bulk removal of every event belonging to a disconnected connection.
// The disconnect RPC doesn't return per-event ids (the row + its
// subcalendars + events are wiped server-side as a unit), so the saga
// has to project the deletion locally by filtering events whose
// subcalendar_id belongs to the dropped connection.
export const EVENTS_REMOVED_BY_CONNECTION = 'calendar/eventsRemovedByConnection';

// Trigger actions (saga watchers)
export const FETCH_EVENTS = 'calendar/fetchEvents';
export const SYNC_CALENDAR = 'calendar/syncCalendar';

// ─── Action Creators ───────────────────────────────────────

export function setEvents(events: CalendarEvent[]) {
  return { type: SET_EVENTS, payload: events } as const;
}

export function addEvent(event: CalendarEvent) {
  return { type: ADD_EVENT, payload: event } as const;
}

export function updateEvent(id: string, changes: Partial<CalendarEvent>) {
  return { type: UPDATE_EVENT, payload: { id, changes } } as const;
}

/**
 * Full-replacement variant of updateEvent. Use when the source is a
 * server response (sync delta) — proto3 omits cleared optionals, so a
 * merge-style update would leave stale fields lingering. Replaces the
 * row with id === event.id; no-op if no row matches.
 */
export function replaceEvent(event: CalendarEvent) {
  return { type: REPLACE_EVENT, payload: event } as const;
}

export function removeEvent(id: string) {
  return { type: REMOVE_EVENT, payload: id } as const;
}

export function setSelectedDate(date: string | null) {
  return { type: SET_SELECTED_DATE, payload: date } as const;
}

export function setViewMode(mode: CalendarViewMode) {
  return { type: SET_VIEW_MODE, payload: mode } as const;
}

export function setCalendarLoading(loading: boolean) {
  return { type: SET_LOADING, payload: loading } as const;
}

export function setCalendarError(error: string | null) {
  return { type: SET_ERROR, payload: error } as const;
}

/**
 * Patch every event whose `subcalendar_id` matches `filter.subcalendar_id`,
 * applying `patch` (a partial CalendarEvent — typically the mirrored
 * subset of the subcalendar mutation: color, assigned_owner_id, and/or
 * a local-only `enabled` flag). Used by updateSubcalendarSaga so that a
 * subcalendar edit projects onto the calendar view without a refetch.
 */
export function calendarEventsPatched(
  filter: { subcalendar_id: string },
  patch: Partial<CalendarEvent>,
) {
  return {
    type: EVENTS_PATCHED_BY_SUBCAL,
    payload: { filter, patch },
  } as const;
}

/**
 * Remove every event belonging to a connection that was just
 * disconnected. Events don't carry connection_id directly, so the
 * saga resolves the connection's subcalendar id list (via
 * listSubcalendars) and passes it in alongside the connection id —
 * the reducer filters by membership in `subcalendarIds`. The
 * connectionId is kept on the payload for trace / observability only;
 * the reducer logic depends solely on subcalendarIds.
 */
export function calendarEventsRemovedByConnection(
  connectionId: string,
  subcalendarIds: string[],
) {
  return {
    type: EVENTS_REMOVED_BY_CONNECTION,
    payload: { connectionId, subcalendarIds },
  } as const;
}

// Trigger action creators
export function fetchEvents(params?: { start?: string; end?: string }) {
  return { type: FETCH_EVENTS, payload: params } as const;
}

export function syncCalendar() {
  return { type: SYNC_CALENDAR } as const;
}

// ─── Action Type Union ─────────────────────────────────────

type CalendarAction =
  | ReturnType<typeof setEvents>
  | ReturnType<typeof addEvent>
  | ReturnType<typeof updateEvent>
  | ReturnType<typeof replaceEvent>
  | ReturnType<typeof removeEvent>
  | ReturnType<typeof setSelectedDate>
  | ReturnType<typeof setViewMode>
  | ReturnType<typeof setCalendarLoading>
  | ReturnType<typeof setCalendarError>
  | ReturnType<typeof calendarEventsPatched>
  | ReturnType<typeof calendarEventsRemovedByConnection>;

// ─── Reducer ───────────────────────────────────────────────

export function calendarReducer(
  state: CalendarState = initialState,
  action: CalendarAction | { type: string; payload?: unknown }
): CalendarState {
  switch (action.type) {
    case SET_EVENTS: {
      const events = (action as ReturnType<typeof setEvents>).payload;
      return {
        ...state,
        events,
        lastSyncAt: Date.now(),
      };
    }

    case ADD_EVENT: {
      const event = (action as ReturnType<typeof addEvent>).payload;
      // Dedup by id — a sync run can land the same row twice (e.g.
      // initial-sync delta racing a follow-up "Sync now") and we don't
      // want duplicates piling up in the events list.
      if (state.events.some((e) => e.id === event.id)) {
        return {
          ...state,
          events: state.events.map((e) => (e.id === event.id ? event : e)),
        };
      }
      return {
        ...state,
        events: [...state.events, event],
      };
    }

    case UPDATE_EVENT: {
      const { id, changes } = (action as ReturnType<typeof updateEvent>).payload;
      return {
        ...state,
        events: state.events.map((e) =>
          e.id === id ? { ...e, ...changes } : e
        ),
      };
    }

    case REPLACE_EVENT: {
      const event = (action as ReturnType<typeof replaceEvent>).payload;
      let touched = false;
      const events = state.events.map((e) => {
        if (e.id !== event.id) return e;
        touched = true;
        return event;
      });
      // No-op when nothing matched — keeps memoized selectors stable.
      if (!touched) return state;
      return { ...state, events };
    }

    case REMOVE_EVENT: {
      const id = (action as ReturnType<typeof removeEvent>).payload;
      return {
        ...state,
        events: state.events.filter((e) => e.id !== id),
      };
    }

    case SET_SELECTED_DATE: {
      const date = (action as ReturnType<typeof setSelectedDate>).payload;
      return { ...state, selectedDate: date };
    }

    case SET_VIEW_MODE: {
      const viewMode = (action as ReturnType<typeof setViewMode>).payload;
      return { ...state, viewMode };
    }

    case SET_LOADING: {
      const loading = (action as ReturnType<typeof setCalendarLoading>).payload;
      return { ...state, loading };
    }

    case SET_ERROR: {
      const error = (action as ReturnType<typeof setCalendarError>).payload;
      return { ...state, error };
    }

    case EVENTS_REMOVED_BY_CONNECTION: {
      const { subcalendarIds } = (
        action as ReturnType<typeof calendarEventsRemovedByConnection>
      ).payload;
      if (!subcalendarIds || subcalendarIds.length === 0) return state;
      const drop = new Set(subcalendarIds);
      const events = state.events.filter((e) => {
        const sid = (e as { subcalendar_id?: string }).subcalendar_id;
        return !sid || !drop.has(sid);
      });
      // Identity-stable no-op when nothing matched.
      if (events.length === state.events.length) return state;
      return { ...state, events };
    }

    case EVENTS_PATCHED_BY_SUBCAL: {
      const { filter, patch } = (action as ReturnType<typeof calendarEventsPatched>).payload;
      const targetId = filter.subcalendar_id;
      if (!targetId) return state;
      let touched = false;
      const events = state.events.map((e) => {
        if ((e as { subcalendar_id?: string }).subcalendar_id !== targetId) return e;
        touched = true;
        return { ...e, ...patch };
      });
      // Skip the state object identity churn when nothing matched —
      // keeps memoized selectors stable on no-op patches (e.g. user
      // toggled a subcalendar with no events cached locally yet).
      if (!touched) return state;
      return { ...state, events };
    }

    default:
      return state;
  }
}
